import { Component, OnInit, Input, Optional, Self, Output, ViewChild, EventEmitter, OnDestroy, Inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import * as uuid from 'uuid';
import { MatDialog } from '@angular/material/dialog';
import { UploadModalComponent } from 'src/app/global/components/forms/upload-modal/upload-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, filter, mergeMap, map, tap, catchError } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { ResizeProfilePictureModalComponent } from 'src/app/global/components/forms/resize-profile-picture-modal/resize-profile-picture-modal.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MobileCameraService } from 'src/app/global/utils/camera/mobile-camera.service';
import { Capacitor } from '@capacitor/core';
import { BottomSheetComponent, BottomSheetOptions } from 'src/app/global/components/common/bottom-sheet/bottom-sheet.component';
import { Icon } from '@bciti/types';
import { UserService } from '../../service/user.abstract.service';

enum MobileBottomSheetAction {
    OpenPhoto = 'OPEN_PHOTO',
    OpenCamera = 'OPEN_CAMERA',
}

@Component({
    selector: 'app-form-user-card-profile-picture',
    templateUrl: './user-cards-profile-picture.component.html',
    styleUrls: ['./user-cards-profile-picture.component.scss'],
})
export class UserCardProfilePictureComponent implements OnInit, OnDestroy, ControlValueAccessor {
    private destroy$ = new Subject();
    public onChange: Function;
    public onTouched: Function;
    public id: string;
    public _value: any = undefined;
    public t: any;
    public loading = false;

    private Icon = Icon;

    @Input() allowModify = true;

    @Input() disableMessages: boolean = false;
    @Input() imgPlaceholder: boolean = true;
    @Input() familyUserId: string = '';
    @Input() disabled: boolean;
    @Input() label: string = null;
    @Input() placeholder: string = this.label;
    @Input() type: 'text' | 'email' | 'password' | 'number' = 'text';
    @Input() validation: boolean = true;
    @Output('change') changeOutput = new EventEmitter<any>();
    @ViewChild('input', { static: true }) input;

    constructor(
        public _bottomSheet: MatBottomSheet,
        public dialog: MatDialog,
        private mobileCameraService: MobileCameraService,
        @Self() @Optional() private ngControl: NgControl,
        private translateService: TranslateService,
        private userService: UserService,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.translateService
            .get('common.profilePicture')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));
    }

    get invalid() {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }
    get valid() {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }
    get pristine() {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }
    get dirty() {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }
    get touched() {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    get value(): any {
        return this._value;
    }
    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.onTouched();
        }
    }

    public ngOnInit(): void {
        this.id = uuid.v4();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public valueChange(v): void {
        if (v !== undefined) {
            this.value = v;
        }
    }

    public writeValue(value: any): void {
        if (value !== undefined) {
            this._value = value;
        }
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public registerOnChange(fn: any): void {
        // Store the provided function as an internal method.
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        // Store the provided function as an internal method.
        this.onTouched = fn;
    }

    openDialog() {
        if (this.isNativeApp()) {
            this.openMobileBottomSheet();
        } else {
            this.openWebDialog();
        }
    }

    private openWebDialog(): void {
        this.dialog
            .open(UploadModalComponent, {
                data: {
                    title: this.t.modalTitle,
                    restrictionMessage: this.t.restrictions,
                    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
                    maxFileSize: 20000000,
                    autoComplete: true,
                },
            })
            .afterClosed()
            .pipe(
                takeUntil(this.destroy$),
                filter((files) => !!files && files.length > 0),
                map((files) => files[0]),
                mergeMap((file) =>
                    this.dialog
                        .open(ResizeProfilePictureModalComponent, {
                            data: { image: file },
                            panelClass: 'modal-scrollable',
                        })
                        .afterClosed(),
                ),
                mergeMap((file) => {
                    if (file) {
                        this.loading = true;
                        if (!!this.familyUserId) {
                            return this.userService.uploadProfilePictureById(this.familyUserId, file);
                        } else {
                            return this.userService.uploadProfilePicture(file);
                        }
                    }
                    return of(undefined);
                }),
                tap((url) => {
                    this.valueChange(url);
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    private openResizerModal(image): void {
        this.dialog
            .open(ResizeProfilePictureModalComponent, {
                data: { image },
                panelClass: 'modal-scrollable',
            })
            .afterClosed()
            .pipe(
                takeUntil(this.destroy$),
                mergeMap((file) => this.userService.uploadProfilePicture(file)),
                tap((url) => this.valueChange(url)),
            )
            .subscribe();
    }

    async openMobilePictureDestination($destination: any): Promise<void> {
        switch ($destination) {
            case MobileBottomSheetAction.OpenCamera: {
                const image = await this.mobileCameraService.openCamera();
                this.openResizerModal(image);
                break;
            }
            case MobileBottomSheetAction.OpenPhoto: {
                const image = await this.mobileCameraService.getPhotoFromLibrary();
                this.openResizerModal(image);
                break;
            }
            default: {
                this.userService.openCameraProfileError(this.t.mobile.unknownError);
                break;
            }
        }
    }

    private openMobileBottomSheet(): void {
        const mobileBottomNavConfig: BottomSheetOptions = {
            cancellable: true,
            actions: [
                {
                    text: this.t.mobile.openCamera,
                    icon: Icon.CAMERA,
                    choiceName: MobileBottomSheetAction.OpenCamera,
                },
                {
                    text: this.t.mobile.openGallery,
                    icon: Icon.IMAGE,
                    choiceName: MobileBottomSheetAction.OpenPhoto,
                },
            ],
        };

        this._bottomSheet
            .open(BottomSheetComponent, {
                data: mobileBottomNavConfig,
                panelClass: 'bciti-bottom-sheet',
            })
            .afterDismissed()
            .pipe(
                takeUntil(this.destroy$),
                tap((destination: MobileBottomSheetAction) => {
                    this.openMobilePictureDestination(destination);
                }),
            )
            .subscribe();
    }

    private isNativeApp(): boolean {
        return Capacitor.isNativePlatform();
    }
}
